<section
  id="home"
  class="relative min-h-screen flex items-center overflow-hidden"
  style="background: var(--theme-bg)"
  aria-label="Hero"
  *transloco="let t"
>
  <!-- ══ Background images — crossfade on theme switch ══════════════════ -->

  <!-- Dark background: visible in dark mode -->
  <div
    class="absolute inset-0 pointer-events-none"
    style="
      z-index: 1;
      background: url('/assets/images/dark-background.png') center / cover
        no-repeat;
      opacity: 0;
      transition: opacity 0.45s ease;
    "
    [style.opacity]="themeService.isDarkMode() ? '1' : '0'"
    aria-hidden="true"
  ></div>

  <!-- Light background: visible in light mode -->
  <div
    class="absolute inset-0 pointer-events-none"
    style="
      z-index: 1;
      background: url('/assets/images/light-background.png') center / cover
        no-repeat;
      opacity: 0;
      transition: opacity 0.45s ease;
    "
    [style.opacity]="themeService.isDarkMode() ? '0' : '1'"
    aria-hidden="true"
  ></div>

  <!-- ══ Gradient overlays — keep text readable ════════════════════════ -->

  <!-- Left-to-right fade: solid bg on left (text area) → transparent on right (shows artwork) -->
  <div
    class="absolute inset-0 pointer-events-none"
    style="
      z-index: 5;
      background: linear-gradient(
        to right,
        var(--theme-bg) 28%,
        rgba(var(--theme-bg-rgb), 0.78) 50%,
        rgba(var(--theme-bg-rgb), 0.12) 100%
      );
    "
    aria-hidden="true"
  ></div>

  <!-- Bottom fade: blends into the next section -->
  <div
    class="absolute inset-0 pointer-events-none"
    style="
      z-index: 5;
      background: linear-gradient(to top, var(--theme-bg) 0%, transparent 30%);
    "
    aria-hidden="true"
  ></div>

  <!-- Top fade: softens the upper edge -->
  <div
    class="absolute inset-0 pointer-events-none"
    style="
      z-index: 5;
      background: linear-gradient(
        to bottom,
        var(--theme-bg) 0%,
        transparent 18%
      );
    "
    aria-hidden="true"
  ></div>

  <!-- ══ Animated elements ═══════════════════════════════════════════════ -->
  <!-- <app-bounce-logo></app-bounce-logo> -->

  <!-- ══ Hero content ════════════════════════════════════════════════════ -->
  <div
    class="relative w-full max-w-7xl mx-auto px-6 lg:px-12 pt-28 pb-36"
    style="z-index: 20"
  >
    <!-- Vertical stacked headline (default) -->
    <!-- <app-hero-headline></app-hero-headline> -->

    <!-- Full-width horizontal headline (swap with the one above to use) -->
    <app-hero-headline-wide></app-hero-headline-wide>

    <p
      class="text-lg lg:text-xl max-w-xl mb-10 reveal stagger-4"
      style="color: var(--theme-text-dim); line-height: 1.75"
    >
      {{ t('home.description') }}
    </p>

    <!-- CTA buttons -->
    <div id="download" class="flex flex-wrap gap-4 mb-14 reveal stagger-5">
      <a
        [href]="freightLoginUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="btn-primary"
        (mousemove)="onMagnet($event)"
        (mouseleave)="onMagnetLeave($event)"
      >
        {{ t('home.freighMarketPlace') }}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="w-4 h-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M7 17L17 7M17 7H7M17 7v10"
          />
        </svg>
      </a>
      <a
        [href]="fleetLoginUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="btn-secondary"
        (mousemove)="onMagnet($event)"
        (mouseleave)="onMagnetLeave($event)"
      >
        {{ t('home.fleetManagment') }}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="w-4 h-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M5 12h14M12 5l7 7-7 7"
          />
        </svg>
      </a>
    </div>

    <!-- App downloads -->
    <div class="reveal stagger-6">
      <p
        class="text-xs uppercase tracking-widest font-semibold mb-4"
        style="color: var(--theme-text-muted)"
      >
        {{ t('home.download') }}
      </p>
      <div class="flex flex-wrap gap-8">
        <div>
          <p class="text-xs mb-2" style="color: var(--theme-text-dim)">
            {{ t('home.freightApp') }}
          </p>
          <a
            href="https://play.google.com/store/apps/details?id=com.aurrica.freight"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-block transition-transform hover:scale-105"
          >
            <img
              src="/assets/images/google-play.png"
              class="h-10 w-auto"
              alt="Get Aurrica Freight on Google Play"
              width="134"
              height="40"
              loading="lazy"
            />
          </a>
        </div>
        <div>
          <p class="text-xs mb-2" style="color: var(--theme-text-dim)">
            {{ t('home.fleetApp') }}
          </p>
          <a
            href="https://play.google.com/store/apps/details?id=com.ituran.mobile"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-block transition-transform hover:scale-105"
          >
            <img
              src="/assets/images/google-play.png"
              class="h-10 w-auto"
              alt="Get Aurrica Fleet on Google Play"
              width="134"
              height="40"
              loading="lazy"
            />
          </a>
        </div>
      </div>
    </div>
  </div>

  <!-- Scroll cue -->
  <div
    class="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 pointer-events-none"
    style="z-index: 20"
    aria-hidden="true"
  >
    <span
      class="text-xs tracking-widest uppercase"
      style="color: var(--theme-text-muted)"
      >Scroll</span
    >
    <svg
      class="w-4 h-4 scroll-caret"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
    >
      <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  </div>
</section>
